import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import rsaController from "../utils/RSA-controller";


import postDataToServer, {
  API_ROUTES,
  deleteFcmTokenOnLogout,
  ensureLocation,
  decryptResponseMessages,
  fetchAndStoreServerPublicKey,
  getProfile,
  registerFcmTokenOnLogin,
  returnToSplash,
} from "../utils/apiClient";
import Popup from "../components/Popup";
import AppConstants from "../utils/appConstants";
import { getWebFcmToken } from "../services/firebaseMessagingService";
import { consumeWhishReturn, parseWhishReturnParams } from "../utils/whishReturn";
import { useToast } from "../contexts/ToastContext";

let initializationInProgress = false;

const isLoginBootstrapFailure = (error) => {
  if (!error) return false;
  if (error.isNetworkError) return true;
  const code = error.code || "";
  if (code === "ERR_NETWORK" || code === "ECONNABORTED" || code === "ERR_CANCELED") {
    return true;
  }
  const msg = String(error.message || "").toLowerCase();
  if (
    msg.includes("network") ||
    msg.includes("timeout") ||
    msg.includes("public key") ||
    msg.includes("failed to fetch")
  ) {
    return true;
  }
  return !error.response && Boolean(error.request);
};

const blockedLaterMessage = (t, payload) => {
  const retryAfter = Number(payload?.RETRY_AFTER ?? payload?.retry_after ?? 0);
  const base = t("you_are_blocked_try_later", {
    ns: "mobile",
    defaultValue: "You are blocked, please try again later",
  });
  if (Number.isFinite(retryAfter) && retryAfter > 0) {
    return `${base} (${Math.ceil(retryAfter / 60)} min)`;
  }
  return base;
};

const readBusinessCode = (payload) => {
  const raw = payload?.CODE ?? payload?.code;
  const code = typeof raw === "number" ? raw : Number(raw);
  return Number.isFinite(code) ? code : null;
};

export default function SignIn() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { success: showSuccess, error: showError } = useToast();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [publicKey, setPublicKey] = useState("");
  const [privateKey, setPrivateKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [show501Popup, setShow501Popup] = useState(false);
  const [show503Popup, setShow503Popup] = useState(false);
  const [show502Popup, setShow502Popup] = useState(false);
  const [blockedMessage, setBlockedMessage] = useState("");
  const signInInFlight = useRef(false);

  useEffect(() => {
  }, [show502Popup]);

  useEffect(() => {
  }, [show503Popup]);

  // Already logged in on /?result=success → enter the app (WhishReturnHandler also handles this)
  useEffect(() => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    const isAuthenticated =
      !!token && token !== "FIRST_TIME_LOGIN" && token.trim() !== "";
    if (!isAuthenticated) return;

    if (parseWhishReturnParams(window.location.search)) return;

    navigate("/pages/dashboard", { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (initializationInProgress) {
      return;
    }
    initializationInProgress = true;

    const initializeServerPublicKey = async () => {
      try {
        // Verify environment variable is loaded (only log if missing for debugging)
        if (!process.env.REACT_APP_SECRET_KEY) {
        }
        
        await rsaController.generateUserKeyPair();

        const userPublicKey = await rsaController.getUserPublicKey();
        const userPrivateKey = await rsaController.getUserPrivateKeyPEM();
        await fetchAndStoreServerPublicKey();
        const serverPublicKey = await rsaController.getServerPublicKeyPEM();
        setPublicKey(userPublicKey);
        setPrivateKey(userPrivateKey);
      } catch (error) {
        initializationInProgress = false;
        returnToSplash();
      }
    };

    initializeServerPublicKey();
  }, []);

  const deleteFcmTokenIfNeeded = async (username) => {
    const normalized = String(username || "").trim();
    if (!normalized) return;
    await deleteFcmTokenOnLogout(normalized);
  };

  const registerFcmTokenIfNeeded = async (username) => {
    const normalized = String(username || "").trim();
    if (!normalized) return;
    const webFcmToken = await getWebFcmToken();
    if (!webFcmToken) return;
    await registerFcmTokenOnLogin(normalized, webFcmToken);
  };

  /** Stash credentials so device verification can auto-login after OTP (Flutter wait-access flow). */
  const stashPendingLoginCredentials = (username, plainPassword) => {
    try {
      sessionStorage.setItem("pending_login_email", String(username || "").trim());
      sessionStorage.setItem("pending_login_password", String(plainPassword || ""));
    } catch (_) {
      /* ignore */
    }
  };

  const handleDeviceVerificationRequired = async () => {
    await deleteFcmTokenIfNeeded(email);
    stashPendingLoginCredentials(email, password);
    setShow501Popup(true);
    setLoading(false);
  };

  const handleLoginOutcome = async (payload) => {
    const code = readBusinessCode(payload);
    if (code == null) return false;

    if (code === AppConstants.loginWaitAccess) {
      await handleDeviceVerificationRequired();
      return true;
    }
    if (code === AppConstants.loginBlocked || code === AppConstants.loginIpBlocked) {
      await deleteFcmTokenIfNeeded(email);
      setBlockedMessage(blockedLaterMessage(t, payload));
      setShow502Popup(true);
      setLoading(false);
      return true;
    }
    if (code === AppConstants.loginIncorrect) {
      await deleteFcmTokenIfNeeded(email);
      setShow503Popup(true);
      setLoading(false);
      return true;
    }
    if (code === AppConstants.loginNotActive) {
      await deleteFcmTokenIfNeeded(email);
      setError(
        t("your_account_has_been_deactivated", {
          ns: "mobile",
          defaultValue: "Your account has been deactivated",
        })
      );
      setLoading(false);
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (!location.state?.loginBlocked) return;
    setBlockedMessage(blockedLaterMessage(t, { RETRY_AFTER: location.state.retryAfter }));
    setShow502Popup(true);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate, t]);

  const handleSignIn = async () => {
    if (signInInFlight.current) return;
    if (!email || !password) {
      // Highlight missing fields with red border instead of showing a message
      setEmailError(!email);
      setPasswordError(!password);
      return;
    }

    signInInFlight.current = true;
    setLoading(true);
    setError("");
    setEmailError(false);
    setPasswordError(false);

    try {
      // Request location on the login click (user gesture) before other async work.
      // Falls back to IP-based coords inside ensureLocation/buildExtraInfo if GPS is denied.
      await ensureLocation();

      const userPublicKey = await rsaController.getUserPublicKey();
      const userPrivateKey = await rsaController.getUserPrivateKeyPEM();
      setPublicKey(userPublicKey);
      setPrivateKey(userPrivateKey);
      const response = await postDataToServer(API_ROUTES.LOGIN, {
        email,
        password,
      });


      const decryptedResult =
        (await decryptResponseMessages(response.data)) || response.data;

      if (await handleLoginOutcome(decryptedResult)) return;

      const result = decryptedResult?.body || decryptedResult;
      if (result !== decryptedResult && (await handleLoginOutcome(result))) return;

      const successCode = readBusinessCode(result) ?? readBusinessCode(decryptedResult);
      if (
        successCode === AppConstants.responseOk ||
        result?.TOKEN ||
        decryptedResult?.TOKEN
      ) {
        localStorage.setItem("userEmail", email);
        localStorage.setItem("isLoggedIn", "true");
        
        // Extract and save TOKEN from login response (matches Flutter behavior)
        // Flutter stores: SharedDataManager.setSharedData(AppConstants.TOKEN, responce['TOKEN'])
        const token = result?.TOKEN || decryptedResult?.TOKEN || result?.token || decryptedResult?.token;
        if (token) {
          localStorage.setItem("token", token);
          sessionStorage.setItem("token", token);
        } else {
        }
        
        // Get user profile to retrieve user_id (matches Flutter behavior)
        try {
          const profileResponse = await getProfile({ withDoc: 1 });
          const profileData = profileResponse?.decryptedData || profileResponse?.data;
          
          // Extract user_id from response.DATA.ID (matches Flutter's UserLogin.fromJson)
          if (profileData?.CODE === AppConstants.responseOk && profileData?.DATA?.ID) {
            const userId = profileData.DATA.ID;
            localStorage.setItem("userId", userId.toString());
            sessionStorage.setItem("userId", userId.toString());
          } else {
          }
        } catch (profileError) {
          // Continue with login even if profile fetch fails
        }

        await registerFcmTokenIfNeeded(email);

        // Device verification is handled entirely by the backend.
        // If the backend requires it, it returns loginWaitAccess (501)
        // which is already handled above (matching Flutter behavior).
        const pendingWhish = consumeWhishReturn();
        navigate("/pages/dashboard", { replace: true });
        if (pendingWhish?.result === "success" || pendingWhish?.result === "failed") {
          if (pendingWhish.result === "success") {
            showSuccess(
              t("transaction_succeeded", {
                ns: "mobile",
                defaultValue: "Transaction succeeded.",
              })
            );
          } else {
            showError(
              t("transaction_failed", {
                ns: "mobile",
                defaultValue: "Transaction failed.",
              })
            );
          }
        }
      } else {
        setError(
          result?.message ||
          result?.error ||
          t("login.errors.loginFailed")
        );
      }
    } catch (error) {
      let errorResult = error.response?.data;
      if (error.response?.data?.MESSAGES) {
        try {
          errorResult =
            (await decryptResponseMessages(error.response.data)) || error.response.data;
        } catch (_) {
          errorResult = null;
        }
      }

      if (errorResult && (await handleLoginOutcome(errorResult))) return;
      if (isLoginBootstrapFailure(error)) {
        returnToSplash();
      } else {
        setError(
          error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          t("login.errors.signInFailed")
        );
      }
    } finally {
      signInInFlight.current = false;
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleSignIn();
  };

  const handleRegister = () => {
    navigate("/informationAccount");
  };

  const handleForgotPassword = (e) => {
    e.preventDefault();
    navigate("/forget-password");
  };

  const close503Popup = () => {
    setShow503Popup(false);
    setError("");
  };

  const close502Popup = () => {
    setShow502Popup(false);
    setError("");
  };

  const close501Popup = () => {
    setShow501Popup(false);
    setError("");
    navigate("/device-verification", { state: { email, password } });
  };

  return (
    <div className="page-container">
      <div className="login-card">
        <h1 className="login-title">{t("login.title")}</h1>
        <form onSubmit={handleSubmit}>
          <label className="input-label">{t("login.username")}</label>
          <input
            type="email"
            placeholder={t("login.usernamePlaceholder")}
            className="login-input"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (e.target.value) {
                setEmailError(false);
              }
            }}
            disabled={loading}
            style={emailError ? { borderColor: "red" } : {}}
          />

          <label className="input-label">{t("login.password")}</label>
          <div className="password-input-wrapper">
            <input
              type={showPassword ? "text" : "password"}
              placeholder={t("login.passwordPlaceholder")}
              className="login-input"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (e.target.value) {
                  setPasswordError(false);
                }
              }}
              disabled={loading}
              autoComplete="current-password"
              style={passwordError ? { borderColor: "red" } : {}}
            />
            <button
              type="button"
              className="password-toggle-btn"
              onClick={() => setShowPassword((prev) => !prev)}
              disabled={loading}
              aria-label={showPassword ? t("login.hidePassword") : t("login.showPassword")}
            >
              <i className={`fa-solid ${showPassword ? "fa-eye-slash" : "fa-eye"}`}></i>
            </button>
          </div>

          {error && (
            <div
              style={{
                color: "red",
                fontSize: "14px",
                marginBottom: "10px",
                padding: "10px",
                backgroundColor: "rgba(255, 230, 230, 0.9)",
                borderRadius: "4px",
                fontWeight: "bold",
              }}
            >
              {error}
            </div>
          )}

          <div className="options">
            <label></label>
          <a href="#" onClick={handleForgotPassword}>
            {t("login.forgotPassword")}
          </a>
          </div>

          <button
            type="submit"
            className="login-btn"
            disabled={loading}
          >
            {loading ? t("login.signingIn") : t("login.loginButton")}
          </button>
          
        </form>

        <div className="new-account">
          {t("login.noAccount")}{" "}
          <a onClick={handleRegister}>{t("login.createAccount")}</a>
        </div>
      </div>
      

      <Popup
        isOpen={show501Popup}
        onClose={close501Popup}
        title={t("popups.deviceVerification.title") || "Device Verification"}
        message={t("popups.accountVerification.message")}
        instruction={t("popups.accountVerification.instruction")}
        buttonText={t("popups.accountVerification.close")}
        icon="fa-triangle-exclamation"
      />

      <Popup
        isOpen={show503Popup}
        onClose={close503Popup}
        title={t("popups.loginFailed.title")}
        message={`${t("username_or_password_error", { ns: "mobile", defaultValue: "Username or Password is incorrect" })} ${t("after_3_attempts_you_will_be_blocked", { ns: "mobile", defaultValue: "After 3 attempts, you will be blocked" })}`}
        buttonText={t("popups.loginFailed.ok")}
        icon="fa-circle-xmark"
      />

      <Popup
        isOpen={show502Popup}
        onClose={close502Popup}
        title={t("you_are_blocked_try_later", {
          ns: "mobile",
          defaultValue: "You are blocked, please try again later",
        })}
        message={blockedMessage || t("you_are_blocked_try_later", {
          ns: "mobile",
          defaultValue: "You are blocked, please try again later",
        })}
        instruction={t("contact_admin", {
          ns: "mobile",
          defaultValue: "Please, contact the admin for any help!",
        })}
        buttonText={t("popups.accountBlocked.close")}
        icon="fa-ban"
      />
    </div>
  );
}
